import { useState, useEffect } from 'react';

const useCases = [
  {
    title: 'Invoice → SAP Integration',
    description: 'Automatically extract invoice data and sync to SAP in real-time',
    icon: 'ri-file-text-line',
    color: 'from-purple-500 to-pink-500',
    stats: '95% faster processing',
  },
  {
    title: 'Excel → ERP Automation',
    description: 'Transform spreadsheet data into structured ERP entries instantly',
    icon: 'ri-file-excel-2-line',
    color: 'from-green-500 to-emerald-500',
    stats: '10,000+ rows/hour',
  },
  {
    title: 'Slack → AI Assistant Workflow',
    description: 'AI-powered assistant that responds to team queries and executes tasks',
    icon: 'ri-slack-line',
    color: 'from-cyan-500 to-blue-500',
    stats: '24/7 availability',
  },
  {
    title: 'Contract Summaries → AI Legal Agent',
    description: 'Intelligent contract analysis and risk assessment automation',
    icon: 'ri-file-shield-2-line',
    color: 'from-orange-500 to-red-500',
    stats: '80% time saved',
  },
  {
    title: 'New Orders → CRM + Email + Billing Sync',
    description: 'Complete order-to-cash automation across all systems',
    icon: 'ri-shopping-cart-line',
    color: 'from-violet-500 to-purple-500',
    stats: 'Zero manual entry',
  },
];

export default function UseCaseCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % useCases.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <section className="relative py-32 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#1a0b2e]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(45deg, rgba(139, 92, 246, 0.1) 25%, transparent 25%),
                             linear-gradient(-45deg, rgba(139, 92, 246, 0.1) 25%, transparent 25%)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Real-World Use Cases
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            See how leading enterprises automate critical workflows
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Main Display */}
          <div className="relative h-[500px] overflow-hidden rounded-3xl">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="absolute inset-0 transition-all duration-700 ease-in-out"
                style={{
                  opacity: index === activeIndex ? 1 : 0,
                  transform: `translateX(${index === activeIndex ? '0%' : index < activeIndex ? '-100%' : '100%'})`,
                  pointerEvents: index === activeIndex ? 'auto' : 'none',
                }}
              >
                <div
                  className="relative h-full rounded-3xl overflow-hidden border border-purple-500/30"
                  style={{
                    background: `linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))`,
                  }}
                >
                  {/* Background Glow */}
                  <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br ${useCase.color} opacity-20 blur-3xl`}
                  />

                  {/* Content */}
                  <div className="relative h-full flex flex-col md:flex-row items-center justify-between p-12 md:p-16">
                    {/* Left: Text Content */}
                    <div className="flex-1 mb-8 md:mb-0 md:pr-12">
                      <div
                        className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${useCase.color} mb-6`}
                      >
                        <span className="text-sm font-bold text-white">{useCase.stats}</span>
                      </div>

                      <h3 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                        {useCase.title}
                      </h3>

                      <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                        {useCase.description}
                      </p>

                      <button className="group px-8 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer">
                        <span className="flex items-center gap-2">
                          View Case Study
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300" />
                        </span>
                      </button>
                    </div>

                    {/* Right: Icon Visualization */}
                    <div className="flex-shrink-0">
                      <div className="relative w-64 h-64 md:w-80 md:h-80">
                        {/* Rotating Ring */}
                        <div
                          className="absolute inset-0 rounded-full border-4 border-dashed border-purple-400/30"
                          style={{
                            animation: 'rotate 20s linear infinite',
                          }}
                        />

                        {/* Icon Container */}
                        <div
                          className={`absolute inset-0 flex items-center justify-center rounded-full bg-gradient-to-br ${useCase.color}`}
                          style={{
                            boxShadow: '0 0 80px rgba(139, 92, 246, 0.4)',
                            animation: 'pulse 3s ease-in-out infinite',
                          }}
                        >
                          <i className={`${useCase.icon} text-8xl md:text-9xl text-white`} />
                        </div>

                        {/* Orbiting Particles */}
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute top-1/2 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2"
                            style={{
                              animation: `orbit ${6 + i}s linear infinite`,
                              animationDelay: `${i * 0.5}s`,
                            }}
                          >
                            <div className={`w-full h-full rounded-full bg-gradient-to-br ${useCase.color}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Dots */}
          <div className="flex items-center justify-center gap-3 mt-8">
            {useCases.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className="group relative cursor-pointer"
              >
                <div
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeIndex ? 'bg-purple-400 scale-125' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
                {index === activeIndex && (
                  <div
                    className="absolute inset-0 rounded-full bg-purple-400 animate-ping"
                    style={{ animationDuration: '2s' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Arrow Navigation */}
          <button
            onClick={() => setActiveIndex((prev) => (prev - 1 + useCases.length) % useCases.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>

          <button
            onClick={() => setActiveIndex((prev) => (prev + 1) % useCases.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes orbit {
          from {
            transform: translate(-50%, -50%) rotate(0deg) translateX(180px) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg) translateX(180px) rotate(-360deg);
          }
        }
      `}</style>
    </section>
  );
}
